import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Row, Col, Button, Form, Alert, Modal, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import ConfirmModal from "../Confirm/ConfirmModal";
import { useConfirm } from "../Confirm/useConfirm";
import { useNotification } from "../../NotificationContext";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import {
  updateSessionById,
  uploadImage,
  fetchPracticeTemplates,
  fetchTemplateVersion,
  exportSessionTemplatePdf,
  emailSessionTemplatePdf,
  getPatientById,
  getPracticesByNumbers,
  updateAppointmentStatusByType,
  getAccountByPatientId,
} from "../../api";
import { getFinComms } from "../../afinanceapi";
import { getPeopleForUser } from "../../apeople";
import NoteRenderer from '../Templates/NoteRenderer';
import { notesToPages } from '../Templates/TemplateBuilder';
import { getUserPreferences } from '../BootNav/userPreferences';
import { NOTE_ACTION_WIDTH } from "./SessionNoteRail";
import ReactQuill from "react-quill";
import ImageAnnotator from "./EditImages";
import {
  FaStop,
} from "react-icons/fa";
import { ReactSketchCanvas } from "react-sketch-canvas";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { getBookingsReturnPath } from "../../utils/bookingsReturn";
import "./EditSession.css";
import "../Communications/Comms.css";

// All non-empty email addresses on a person record (primary first).
const personEmails = (p) =>
  ["email", "email2", "email3", "email4", "email5"]
    .map((f) => (p?.[f] ?? "").trim())
    .filter(Boolean);

const personDisplayName = (p) =>
  `${p?.title ?? ""} ${p?.firstName ?? ""} ${p?.lastName ?? ""}`
    .replace(/\s+/g, " ")
    .trim();

// --- Voice recorder hook (modal-driven) ---
const useVoiceRecorder = () => {
  const { transcript, resetTranscript, listening } = useSpeechRecognition();
  const start = () => {
    if (!SpeechRecognition.browserSupportsSpeechRecognition()) return;
    resetTranscript();
    SpeechRecognition.startListening({
      continuous: true,
      interimResults: true,
      language: "en-US",
    });
  };
  const stop = () => {
    SpeechRecognition.stopListening();
    return (transcript || "").trim();
  };
  const clear = () => resetTranscript();
  return { listening, transcript, start, stop, clear };
};

// --- Recorder Modal ---
const RecorderModal = ({ show, onClose, onDone, vr }) => {
  useEffect(() => {
    if (show) vr.clear();
  }, [show]); // clear transcript on open

  const handleStart = () => vr.start();
  const handleStop = () => vr.stop();
  const handleUse = () => {
    const text = (vr.transcript || "").trim();
    if (vr.listening) vr.stop();
    onDone?.(text);
    onClose?.();
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">Voice Recorder</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column align-items-center gap-3">
          <div className={`soundwave ${vr.listening ? "live" : ""}`}>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div className="w-100">
            <Form.Label className="fw-semibold">Live transcript</Form.Label>
            <div
              aria-live="polite"
              style={{
                minHeight: 90,
                border: "1px solid var(--acc-border-color)",
                borderRadius: 8,
                padding: "0.5rem 0.75rem",
                background: "var(--acc-header-bg)",
                whiteSpace: "pre-wrap",
              }}
            >
              {vr.transcript || (
                <span className="text-muted">Say something…</span>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div className="d-flex gap-2">
          {!vr.listening ? (
            <Button variant="outline-primary" onClick={handleStart}>
              <i className="fa-light fa-microphone me-2"></i> Start
            </Button>
          ) : (
            <Button variant="danger" onClick={handleStop}>
              <FaStop className="me-2" /> Stop
            </Button>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUse}
            disabled={!vr.transcript?.trim()}
          >
            Use Transcript
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

// --- Handwriting Modal (fullscreen canvas for stylus/touch input) ---
const HandwritingModal = ({ show, onHide, onSave, existingImage }) => {
  const canvasRef = useRef(null);
  const [strokeColor, setStrokeColor] = useState("#000000");
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [isEraser, setIsEraser] = useState(false);
  const [isHighlighter, setIsHighlighter] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);

  // Load existing image as background when modal opens
  useEffect(() => {
    if (show && existingImage) {
      setBackgroundImage(existingImage);
    } else if (!show) {
      setBackgroundImage(null);
    }
  }, [show, existingImage]);

  const handleSave = async () => {
    if (canvasRef.current) {
      try {
        // Check if user drew any new strokes
        const paths = await canvasRef.current.exportPaths();
        const hasNewStrokes = paths && paths.length > 0;

        // If there's a background but no new strokes, keep the original unchanged
        if (backgroundImage && !hasNewStrokes) {
          onSave(backgroundImage);
          onHide();
          return;
        }

        // Export the strokes layer at native canvas dimensions
        const strokesDataUrl = await canvasRef.current.exportImage("png");

        // If there's a background image, merge it with the strokes
        if (backgroundImage && hasNewStrokes) {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          // Load strokes image first to get canvas dimensions
          const strokesImg = new Image();
          strokesImg.crossOrigin = "anonymous";

          await new Promise((resolve, reject) => {
            strokesImg.onload = resolve;
            strokesImg.onerror = reject;
            strokesImg.src = strokesDataUrl;
          });

          // Load background image
          const bgImg = new Image();
          bgImg.crossOrigin = "anonymous";

          await new Promise((resolve, reject) => {
            bgImg.onload = resolve;
            bgImg.onerror = reject;
            bgImg.src = backgroundImage;
          });

          // Use strokes canvas dimensions as the base
          canvas.width = strokesImg.width || 800;
          canvas.height = strokesImg.height || 600;

          // Calculate aspect-ratio-preserving dimensions for background
          const bgAspect = bgImg.width / bgImg.height;
          const canvasAspect = canvas.width / canvas.height;

          let drawWidth, drawHeight, offsetX, offsetY;

          if (bgAspect > canvasAspect) {
            // Background is wider - fit to width
            drawWidth = canvas.width;
            drawHeight = canvas.width / bgAspect;
            offsetX = 0;
            offsetY = (canvas.height - drawHeight) / 2;
          } else {
            // Background is taller - fit to height
            drawHeight = canvas.height;
            drawWidth = canvas.height * bgAspect;
            offsetX = (canvas.width - drawWidth) / 2;
            offsetY = 0;
          }

          // Draw background centered and aspect-ratio preserved (keep transparent)
          ctx.drawImage(bgImg, offsetX, offsetY, drawWidth, drawHeight);

          // Draw strokes on top at their native size (no scaling needed)
          if (strokesImg.width > 0 && strokesImg.height > 0) {
            ctx.drawImage(strokesImg, 0, 0);
          }

          // Export merged image
          const mergedDataUrl = canvas.toDataURL("image/png");
          onSave(mergedDataUrl);
        } else {
          // No background, just use the strokes
          onSave(strokesDataUrl);
        }

        onHide();
      } catch (err) {
        console.error("Failed to export canvas:", err);
      }
    }
  };

  const handleUndo = () => canvasRef.current?.undo();
  const handleRedo = () => canvasRef.current?.redo();
  const handleClear = () => canvasRef.current?.clearCanvas();

  const toggleEraser = () => {
    setIsEraser(!isEraser);
    setIsHighlighter(false);
    if (canvasRef.current) {
      canvasRef.current.eraseMode(!isEraser);
    }
  };

  const toggleHighlighter = () => {
    const next = !isHighlighter;
    setIsHighlighter(next);
    setIsEraser(false);
    if (canvasRef.current) canvasRef.current.eraseMode(false);
    if (next) {
      setStrokeColor("rgba(255,255,0,0.4)");
      setStrokeWidth(20);
    } else {
      setStrokeColor("#000000");
      setStrokeWidth(3);
    }
  };

  const colorOptions = ["#000000", "#0f3490", "#dc3545", "#198754", "#6c757d"];
  const strokeOptions = [2, 3, 5, 8];

  return (
    <Modal
      show={show}
      onHide={onHide}
      fullscreen
      className="handwriting-modal"
      backdrop="static"
    >
      <Modal.Header className="handwriting-modal-header py-2" style={{ backgroundColor: "#CFE2FF" }}>
        <div className="d-flex align-items-center gap-3 flex-wrap w-100">
          {/* Color picker */}
          <div className="d-flex align-items-center gap-1">
            {colorOptions.map((color) => (
              <button
                key={color}
                type="button"
                className={`color-btn ${strokeColor === color && !isEraser && !isHighlighter ? "active" : ""}`}
                style={{ backgroundColor: color }}
                onClick={() => {
                  setStrokeColor(color);
                  setIsEraser(false);
                  setIsHighlighter(false);
                  setStrokeWidth(3);
                  canvasRef.current?.eraseMode(false);
                }}
                aria-label={`Color ${color}`}
              />
            ))}
          </div>

          {/* Stroke width */}
          <div className="d-flex align-items-center gap-1">
            {strokeOptions.map((width) => (
              <button
                key={width}
                type="button"
                className={`stroke-btn ${strokeWidth === width && !isHighlighter ? "active" : ""}`}
                onClick={() => {
                  setStrokeWidth(width);
                  setIsHighlighter(false);
                }}
                aria-label={`Stroke width ${width}`}
              >
                <span
                  style={{ width: width * 2, height: width * 2 }}
                  className="stroke-preview"
                />
              </button>
            ))}
          </div>

          {/* Tools */}
          <div className="d-flex align-items-center gap-2">
            <OverlayTrigger placement="top" overlay={<Tooltip>Highlighter</Tooltip>}>
              <Button
                variant={isHighlighter ? "warning" : "outline-primary"}
                size="sm"
                onClick={toggleHighlighter}
              >
                <i className="fa-solid fa-highlighter" />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Eraser</Tooltip>}>
              <Button
                variant={isEraser ? "primary" : "outline-primary"}
                size="sm"
                onClick={toggleEraser}
              >
                <i className="fa-solid fa-eraser" />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Undo</Tooltip>}>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleUndo}
              >
                <i className="fa-solid fa-rotate-left" />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Redo</Tooltip>}>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleRedo}
              >
                <i className="fa-solid fa-rotate-right" />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Clear all</Tooltip>}>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleClear}
              >
                <i className="fa-solid fa-trash" />
              </Button>
            </OverlayTrigger>
          </div>

          {/* Actions */}
          <div className="d-flex align-items-center gap-2 ms-auto">
            <Button variant="outline-primary" size="sm" onClick={onHide}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave}>
              Save Drawing
            </Button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="p-0 handwriting-canvas-container" style={{ overflowY: "auto" }}>
        <div style={{ width: "100%", height: "600vh", position: "relative" }}>
          <ReactSketchCanvas
            ref={canvasRef}
            strokeWidth={strokeWidth}
            strokeColor={isEraser ? "transparent" : strokeColor}
            canvasColor="transparent"
            backgroundImage={backgroundImage || ""}
            preserveBackgroundImageAspectRatio="xMidYMid meet"
            style={{
              width: "100%",
              height: "100%",
            }}
            allowOnlyPointerType="all"
            withTimestamp={true}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

// --- Inline Write Section (canvas for handwriting inside the bordered layout) ---
const InlineWriteSection = forwardRef(({ onSave, existingImage, onExpand, isFullScreen }, ref) => {
  const canvasRef = useRef(null);
  const [strokeColor, setStrokeColor] = useState("#000000");
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [isEraser, setIsEraser] = useState(false);
  const [isHighlighter, setIsHighlighter] = useState(false);

  const colorOptions = ["#000000", "#0f3490", "#dc3545", "#198754", "#6c757d"];
  const strokeOptions = [2, 3, 5, 8];

  // The page the live strokes sit on top of — the note's SAVED image, never a
  // composite this component produced.
  //
  // `capture` merges this background with the current strokes and hands the
  // result up, and that result comes straight back down as `existingImage`.
  // Feeding it into the canvas baked every stroke into the background while the
  // stroke's path was still on the canvas, so undo / redo / the eraser — which
  // only ever touch paths — looked dead: the line they removed was still there,
  // in the background behind them. Pinning the background keeps every capture a
  // plain merge of this image with whatever paths currently exist.
  const lastCaptureRef = useRef(null);
  const [canvasBg, setCanvasBg] = useState(existingImage || "");
  useEffect(() => {
    // Our own composite echoing back — leave the background alone.
    if (existingImage && existingImage === lastCaptureRef.current) return;
    setCanvasBg(existingImage || "");
  }, [existingImage]);

  const toggleEraser = () => {
    const next = !isEraser;
    setIsEraser(next);
    setIsHighlighter(false);
    canvasRef.current?.eraseMode(next);
  };

  const toggleHighlighter = () => {
    const next = !isHighlighter;
    setIsHighlighter(next);
    setIsEraser(false);
    canvasRef.current?.eraseMode(false);
    if (next) { setStrokeColor("rgba(255,255,0,0.4)"); setStrokeWidth(20); }
    else { setStrokeColor("#000000"); setStrokeWidth(3); }
  };

  // Merges current strokes with background and returns the composite image.
  // Does NOT clear the canvas so the user can keep editing after navigating away and back.
  const capture = async () => {
    if (!canvasRef.current) return canvasBg || null;
    try {
      const paths = await canvasRef.current.exportPaths();
      const hasNewStrokes = paths && paths.length > 0;
      // Nothing drawn — the saved page already IS the current image, and
      // exporting here would write a blank canvas over it.
      if (!hasNewStrokes) return canvasBg || null;
      const strokesDataUrl = await canvasRef.current.exportImage("png");
      let result;
      if (canvasBg) {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const strokesImg = new Image();
        strokesImg.crossOrigin = "anonymous";
        await new Promise((res, rej) => { strokesImg.onload = res; strokesImg.onerror = rej; strokesImg.src = strokesDataUrl; });
        const bgImg = new Image();
        bgImg.crossOrigin = "anonymous";
        await new Promise((res, rej) => { bgImg.onload = res; bgImg.onerror = rej; bgImg.src = canvasBg; });
        canvas.width = strokesImg.width || 800;
        canvas.height = strokesImg.height || 600;
        const bgAspect = bgImg.width / bgImg.height;
        const canvasAspect = canvas.width / canvas.height;
        let dw, dh, ox, oy;
        if (bgAspect > canvasAspect) { dw = canvas.width; dh = canvas.width / bgAspect; ox = 0; oy = (canvas.height - dh) / 2; }
        else { dh = canvas.height; dw = canvas.height * bgAspect; ox = (canvas.width - dw) / 2; oy = 0; }
        ctx.drawImage(bgImg, ox, oy, dw, dh);
        if (strokesImg.width > 0) ctx.drawImage(strokesImg, 0, 0);
        result = canvas.toDataURL("image/png");
      } else {
        result = strokesDataUrl;
      }
      lastCaptureRef.current = result;
      onSave(result);
      return result;
    } catch (err) { console.error("Failed to capture canvas:", err); return canvasBg || null; }
  };

  // Debounced so a burst of quick strokes doesn't run the compositing work
  // once per stroke; 400ms is well inside the time it takes to switch notes.
  const strokeTimerRef = useRef(null);
  const handleStroke = () => {
    if (strokeTimerRef.current) clearTimeout(strokeTimerRef.current);
    strokeTimerRef.current = setTimeout(() => { capture(); }, 400);
  };

  // Flush a pending debounce, then capture. Callers that are about to leave the
  // canvas use this so the last stroke can't be lost to the timer.
  const flush = async () => {
    if (strokeTimerRef.current) {
      clearTimeout(strokeTimerRef.current);
      strokeTimerRef.current = null;
    }
    return capture();
  };

  useEffect(() => () => {
    if (strokeTimerRef.current) clearTimeout(strokeTimerRef.current);
  }, []);

  // `capture` alone can't answer "did the user actually draw?" — with no
  // existing image it happily exports a blank canvas. Callers deciding whether
  // there is anything worth saving need this instead.
  const hasStrokes = async () => {
    if (!canvasRef.current) return false;
    try {
      const paths = await canvasRef.current.exportPaths();
      return Array.isArray(paths) && paths.length > 0;
    } catch {
      return false;
    }
  };

  useImperativeHandle(ref, () => ({ capture, hasStrokes, flush }));

  return (
    <div>
      {/* Toolbar */}
      <div className="handwriting-modal-header d-flex align-items-center gap-2 flex-wrap p-2 mb-1" style={{ backgroundColor: "var(--acc-brand-tint, #CFE2FF)" }}>
        <div className="d-flex align-items-center gap-1">
          {colorOptions.map((color) => (
            <button key={color} type="button"
              className={`color-btn ${strokeColor === color && !isEraser && !isHighlighter ? "active" : ""}`}
              style={{ backgroundColor: color }}
              onClick={() => { setStrokeColor(color); setIsEraser(false); setIsHighlighter(false); setStrokeWidth(3); canvasRef.current?.eraseMode(false); }}
            />
          ))}
        </div>
        <div className="d-flex align-items-center gap-1">
          {strokeOptions.map((w) => (
            <button key={w} type="button"
              className={`stroke-btn ${strokeWidth === w && !isHighlighter ? "active" : ""}`}
              onClick={() => { setStrokeWidth(w); setIsHighlighter(false); }}
            >
              <span style={{ width: w * 2, height: w * 2 }} className="stroke-preview" />
            </button>
          ))}
        </div>
        <div className="d-flex align-items-center gap-1">
          <OverlayTrigger placement="top" overlay={<Tooltip>Highlighter</Tooltip>}>
            <Button variant={isHighlighter ? "warning" : "outline-primary"} size="sm" onClick={toggleHighlighter}><i className="fa-solid fa-highlighter" /></Button>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>Eraser</Tooltip>}>
            <Button variant={isEraser ? "primary" : "outline-primary"} size="sm" onClick={toggleEraser}><i className="fa-solid fa-eraser" /></Button>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>Undo</Tooltip>}>
            <Button variant="outline-primary" size="sm" onClick={() => canvasRef.current?.undo()}><i className="fa-solid fa-rotate-left" /></Button>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>Redo</Tooltip>}>
            <Button variant="outline-primary" size="sm" onClick={() => canvasRef.current?.redo()}><i className="fa-solid fa-rotate-right" /></Button>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>Clear</Tooltip>}>
            <Button variant="outline-primary" size="sm" onClick={() => canvasRef.current?.clearCanvas()}><i className="fa-solid fa-trash" /></Button>
          </OverlayTrigger>
        </div>
        <div className="ms-auto d-flex align-items-center gap-2">
          <OverlayTrigger placement="top" overlay={<Tooltip>{isFullScreen ? "Collapse" : "Expand"}</Tooltip>}>
            <Button variant="link" className="text-primary text-decoration-none p-0 px-2" onClick={onExpand} aria-label={isFullScreen ? "Collapse" : "Expand"}>
              <i className={`fa-solid ${isFullScreen ? "fa-arrows-minimize" : "fa-arrows-maximize"} fs-5`} />
            </Button>
          </OverlayTrigger>
        </div>
      </div>
      {/* Canvas */}
      <div className="handwriting-canvas-container" style={{ width: "100%", height: "1200px", overflow: "hidden" }}>
        <ReactSketchCanvas
          ref={canvasRef}
          strokeWidth={isHighlighter ? 20 : strokeWidth}
          strokeColor={isEraser ? "transparent" : (isHighlighter ? "rgba(255,255,0,0.4)" : strokeColor)}
          canvasColor="transparent"
          backgroundImage={canvasBg}
          preserveBackgroundImageAspectRatio="xMidYMid meet"
          style={{ width: "100%", height: "100%" }}
          allowOnlyPointerType="all"
          withTimestamp={true}
          onStroke={handleStroke}
        />
      </div>
    </div>
  );
});

// Six-checkbox block for the PDF inclusion flags. Used in Download Options + Email modals.
// Template Title is rendered separately at the top with an editable text input
// so the user can override the title that appears on the PDF.
const PDF_OPTION_FIELDS = [
  ["includePatientName",   "Patient Name"],
  ["includeIdNumber",      "ID Number"],
  ["includeDob",           "Date of Birth"],
  ["includeMedicalAid",    "Medical Aid / IOD Info"],
  ["includeSessionDate",   "Session Date"],
];
const PdfOptionCheckboxes = ({ options, setOptions, templateTitle, setTemplateTitle }) => {
  const titleOn = options.includeTemplateTitle !== false;
  return (
    <>
      <div className="mb-3">
        <Form.Check
          type="checkbox"
          id="pdfopt-includeTemplateTitle"
          label="Template Title"
          checked={titleOn}
          onChange={(e) =>
            setOptions((prev) => ({ ...prev, includeTemplateTitle: e.target.checked }))
          }
        />
        {titleOn && (
          <Form.Control
            type="text"
            size="sm"
            className="mt-1"
            placeholder="Title shown on the PDF"
            value={templateTitle || ""}
            onChange={(e) => setTemplateTitle(e.target.value)}
            aria-label="PDF title"
          />
        )}
      </div>
      <Row className="g-2">
        {PDF_OPTION_FIELDS.map(([key, label]) => (
          <Col xs={6} key={key}>
            <Form.Check
              type="checkbox"
              id={`pdfopt-${key}`}
              label={label}
              checked={options[key] !== false}
              onChange={(e) => setOptions((prev) => ({ ...prev, [key]: e.target.checked }))}
            />
          </Col>
        ))}
      </Row>
    </>
  );
};

export const UpdateSession = forwardRef(({
  sessionId,
  patientId,
  appointmentId,
  serviceName,
  appointmentStart,
  appointmentStatus,
  sections = {},
  notepadContentR = "",
  notepadContentType = "",
  handwritingImageR = null,
  templateId,
  practiceId,
  resourceId,
  activeSection,
  // Called after every explicit save with (sessionId, { mode }). The Notes
  // section refreshes its list from it; nothing navigates away on a save.
  onSaved,
  mediaFilesR = [],
  capturedValuesR = {},
  sessionsTemplateVersionR = null,
  updatedAtR = null,
  // One note = one kind. `legacyBundle` marks a document written before the
  // split that holds several kinds at once; those keep the section nav so
  // none of their already-saved content becomes unreachable.
  sessionType,
  legacyBundle = false,
  // Whether "Save and invoice" is offered: a registered patient's current
  // appointment that has not been invoiced yet. The Notes section decides.
  canInvoice = false,
  alreadyInvoiced = false,
  serviceId,
  // Called once a save has marked the appointment Completed, so the owner
  // can update its copy of the appointment.
  onAppointmentCompleted,
}, ref) => {
  const [notes, setNotes] = useState([]);
  // Set by the user-facing edit handlers; consumed by the imperative saveNow()
  // so moving between notes doesn't re-save an untouched one on every click.
  const dirtyRef = useRef(false);
  const markDirty = () => { dirtyRef.current = true; };
  const [notepadContent, setNotepadContent] = useState("");
  const [handwritingImage, setHandwritingImage] = useState(null);
  const [showHandwritingModal, setShowHandwritingModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isNarrow, setIsNarrow] = useState(window.innerWidth <= 1024);
  const [isLandscape, setIsLandscape] = useState(
    window.matchMedia("(orientation: landscape)").matches,
  );
  const [isSaving, setIsSaving] = useState(false);
  // Opt-in: mark the linked appointment Complete on save. Default driven by
  // the user's preferences.autoCompleteAppointments flag (off unless set).
  // Hidden for ad-hoc notes (no appointmentId) and already-completed appts.
  const [completeAppointment, setCompleteAppointment] = useState(
    !!getUserPreferences().autoCompleteAppointments,
  );
  const { toast, showToast, hideToast } = useToast();
  const { confirm, confirmProps } = useConfirm();
  const { addNotification } = useNotification() || {};
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    templateId || "",
  );
  const quillRef = useRef(null);
  const annotatorRefs = useRef([]);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [hasLoadedExistingData, setHasLoadedExistingData] = useState(false);
  const [mediaImages, setMediaImages] = useState([]); // strings (dataURL or http)
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [camError, setCamError] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const loadedNotepadRef = useRef(false);
  const initialTemplateIdRef = useRef(templateId || "");
  const Navigate = useNavigate();
  // Pick the section to open based on what content the session actually has.
  // Priority follows the tab order in the nav: Template > Note Pad > Write > Media.
  // Template hit can come from either capturedValues (current schema) or the
  // legacy sections[] array (older sessions). Falls back to template if a
  // templateId is set, otherwise notepad.
  const resolveDefaultSection = (tid, hwImg, notepad, captured, sectionsArr, media, overrideSection) => {
    if (overrideSection) return overrideSection;
    const hasCaptured = captured && Object.keys(captured).length > 0;
    const hasSections = Array.isArray(sectionsArr) && sectionsArr.length > 0;
    const hasTemplateData = hasCaptured || hasSections;
    const hasNotepad = typeof notepad === "string" && notepad.trim().length > 0;
    const hasHandwriting = !!hwImg;
    const hasMedia = Array.isArray(media) && media.length > 0;
    if (hasTemplateData) return "apptTemplate";
    if (hasNotepad) return "apptNotepad";
    if (hasHandwriting) return "write";
    if (hasMedia) return "apptMedia";
    return tid ? "apptTemplate" : "apptNotepad";
  };
  const [localSection, setLocalSection] = useState(
    resolveDefaultSection(templateId, handwritingImageR, notepadContentR, capturedValuesR, sections, mediaFilesR, activeSection)
  );
  const [isFullScreen, setIsFullScreen] = useState(false);
  const inlineWriteRef = useRef(null);

  // NoteRenderer fill mode state
  const [capturedValues, setCapturedValues] = useState({});
  const [templatePages, setTemplatePages] = useState([]);
  const [templateVersion, setTemplateVersion] = useState(0);
  // Set when the session was filled against a template version that we
  // couldn't load from templateVersions (deleted, never published, etc.).
  // Shape: { storedVersion: number } — banner shown above the renderer.
  const [versionWarning, setVersionWarning] = useState(null);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [serverTimestamp, setServerTimestamp] = useState(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  // People (practitioners) in the logged-in user's practice(s), for the "To"
  // typeahead. Loaded when the email modal opens.
  const [people, setPeople] = useState([]);
  const [toSuggestOpen, setToSuggestOpen] = useState(false);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [pdfOptions, setPdfOptions] = useState({
    includeTemplateTitle: true,
    includePatientName: true,
    includeIdNumber: true,
    includeDob: true,
    includeMedicalAid: true,
    includeSessionDate: true,
  });
  // Editable PDF title. Defaults to the selected template's title; the user
  // can override it before downloading or emailing. Re-syncs whenever the
  // selected template changes so each new template loads with its own default.
  const [pdfTemplateTitle, setPdfTemplateTitle] = useState("");
  useEffect(() => {
    const tpl = templates.find((t) => t._id === selectedTemplateId);
    setPdfTemplateTitle(tpl?.templateTitle || "");
  }, [selectedTemplateId, templates]);
  const lastBackfilledSessionId = useRef(null);
  const [voiceTargetId, setVoiceTargetId] = useState(null);
  const [hideButtonText, setHideButtonText] = useState(false);

  useEffect(() => {
    const compute = () => {
      const portrait = window.matchMedia("(orientation: portrait)").matches;
      setHideButtonText(window.innerWidth <= 768 && portrait);
    };
    compute();
    const mql = window.matchMedia("(orientation: portrait)");
    const onMql = () => compute();
    window.addEventListener("resize", compute);
    if (mql.addEventListener) mql.addEventListener("change", onMql);
    else mql.addListener(onMql);
    return () => {
      window.removeEventListener("resize", compute);
      if (mql.removeEventListener) mql.removeEventListener("change", onMql);
      else mql.removeListener(onMql);
    };
  }, []);

  const handleSectionChange = async (key) => {
    // Capture write canvas while still visible before hiding it — exportImage fails on display:none
    if (localSection === "write" && key !== "write" && inlineWriteRef.current) {
      await inlineWriteRef.current.flush();
    }
    setLocalSection(key);
  };

  const handleExpand = async () => {
    // Capture canvas before fullscreen toggle — portaling remounts the tree, which resets the canvas
    if (inlineWriteRef.current) await inlineWriteRef.current.capture();
    setIsFullScreen((f) => !f);
  };

  // A note's kind decides which editor body shows. An explicit kind wins over
  // the page's section state, so a "media" note can't open on the template tab.
  // Legacy bundles are exempt — they genuinely hold several kinds.
  const SECTION_FOR_KIND = {
    template: "apptTemplate",
    note: "apptNotepad",
    write: "write",
    media: "apptMedia",
  };

  // Sync localSection from activeSection prop
  useEffect(() => {
    if (!legacyBundle && sessionType && SECTION_FOR_KIND[sessionType]) {
      setLocalSection(SECTION_FOR_KIND[sessionType]);
      return;
    }
    if (activeSection) setLocalSection(activeSection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, sessionType, legacyBundle]);

  const navItems = [
    { key: "apptTemplate", label: "Template", icon: "fa-light fa-file-waveform" },
    { key: "apptNotepad",  label: "Note Pad", icon: "fa-light fa-clipboard-medical" },
    { key: "write",        label: "Write",    icon: "fa-light fa-pen-nib" },
    { key: "apptMedia",    label: "Media",    icon: "fa-light fa-camera" },
  ];

  const safeHtml = (v) => (typeof v === "string" ? v : v ? String(v) : "");

  // shared normalizer
  const normalizeNotes = (incoming) => {
    const arr = Array.isArray(incoming?.sections)
      ? incoming.sections
      : Array.isArray(incoming)
        ? incoming
        : Array.isArray(incoming?.notes)
          ? incoming.notes
          : [];

    return arr.map((n) =>
      n.sessionsFieldType === "multiselect"
        ? {
            ...n,
            sessionsSectionNotes: {
              availableOptions: Array.isArray(
                n.sessionsSectionNotes?.availableOptions,
              )
                ? n.sessionsSectionNotes.availableOptions
                : Array.isArray(n.sessionsSectionNotes)
                  ? n.sessionsSectionNotes
                  : [],
              selectedOptions: Array.isArray(
                n.sessionsSectionNotes?.selectedOptions,
              )
                ? n.sessionsSectionNotes.selectedOptions
                : [],
            },
          }
        : {
            ...n,
            sessionsSectionNotes:
              typeof n.sessionsSectionNotes === "string"
                ? n.sessionsSectionNotes
                : (n.sessionsSectionNotes ?? "") + "",
          },
    );
  };

  // Build capturedValues from legacy sections[] by matching labels to page element labels.
  // Used when a session has no capturedValues (pre-v16) or when stored IDs no longer match
  // the template (e.g. due to unstable uid() generation before stable IDs were introduced).
  const buildCapturedValuesFromSections = (secs, pages) => {
    const sectionsArr = Array.isArray(secs) ? secs : [];
    const values = {};
    for (const page of (pages || [])) {
      for (const item of (page.items || [])) {
        const elements = item.kind === 'group' ? (item.children || []) : [item];
        for (const el of elements) {
          const sec = sectionsArr.find(s => s.sessionsSection === el.label);
          if (!sec) continue;
          if (sec.sessionsFieldType === 'multiselect') {
            values[el.id] = Array.isArray(sec.sessionsSectionNotes?.selectedOptions)
              ? sec.sessionsSectionNotes.selectedOptions : [];
          } else if (sec.sessionsFieldType === 'image') {
            values[el.id] = sec.sessionsImageURL || null;
          } else {
            values[el.id] = typeof sec.sessionsSectionNotes === 'string'
              ? sec.sessionsSectionNotes : '';
          }
        }
      }
    }
    return values;
  };

  // Loads templatePages + templateVersion for a session, honouring the
  // version it was filled against (sessionsTemplateVersionR). Falls back to
  // the current template pages when no version is stored or the version
  // snapshot can't be fetched, in which case versionWarning is set so the
  // user sees a banner.
  const applyTemplatePagesForSession = async (sel, storedVersion) => {
    const currentPages = sel
      ? (Array.isArray(sel.pages) && sel.pages.length > 0 ? sel.pages : notesToPages(sel.notes))
      : [];

    if (sel && typeof storedVersion === "number" && storedVersion > 0) {
      try {
        const ver = await fetchTemplateVersion(sel._id, storedVersion);
        if (ver && Array.isArray(ver.pages) && ver.pages.length > 0) {
          setTemplatePages(ver.pages);
          setTemplateVersion(storedVersion);
          setVersionWarning(null);
          return;
        }
        setVersionWarning({ storedVersion });
      } catch (err) {
        setVersionWarning({ storedVersion });
      }
    } else {
      setVersionWarning(null);
    }

    setTemplatePages(currentPages);
    setTemplateVersion(storedVersion || sel?.currentVersion || 0);
  };

  // media init/merge
  useEffect(() => {
    const urls = (mediaFilesR || [])
      .map((f) => (typeof f === "string" ? f : f?.fileUrl))
      .filter(Boolean);

    if (!urls.length) return;
    setMediaImages((prev) => {
      if (!prev.length) return urls;
      const seen = new Set(urls);
      return [...urls, ...prev.filter((u) => !seen.has(u))];
    });
  }, [sessionId, mediaFilesR]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startStream = async () => {
    try {
      stopStream();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCamError(null);
    } catch (e) {
      setCamError(e.message || "Camera access failed");
    }
  };

  useEffect(() => {
    if (localSection === "apptMedia") startStream();
    else stopStream();
    return stopStream;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSection, facingMode]);

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    markDirty();
    setMediaImages((prev) => [dataUrl, ...prev]);
  };

  const removePhoto = (idx) => {
    markDirty();
    setMediaImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const flipCamera = () =>
    setFacingMode((m) => (m === "environment" ? "user" : "environment"));

  // Recorder wiring
  const vr = useVoiceRecorder();
  const [recOpen, setRecOpen] = useState(false);
  const [recTarget, setRecTarget] = useState(null); // { type:'template'|'notepad', index? }

  const openRecorderForTemplate = (index) => {
    setRecTarget({ type: "template", index });
    setRecOpen(true);
  };
  const openRecorderForNotepad = () => {
    setRecTarget({ type: "notepad" });
    setRecOpen(true);
  };
  const closeRecorder = () => {
    if (vr.listening) vr.stop();
    setRecOpen(false);
    setRecTarget(null);
    setVoiceTargetId(null); // clears NoteRenderer field target too
  };

  const insertToQuill = (value) => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    quill.focus();
    const end = quill.getLength();
    const needsSpace = end > 1 && /\S/.test(quill.getText(end - 2, 1));
    const text = (needsSpace ? " " : "") + (value || "");
    quill.insertText(end, text, "user");
    quill.setSelection(end + text.length, 0, "user");
    setTimeout(() => setNotepadContent(quill.root.innerHTML), 0);
  };

  const applyTranscriptToTarget = (text) => {
    const cleaned = (text || "").trim();
    if (!cleaned) return;
    if (voiceTargetId) {
      // NoteRenderer field voice path — applies transcript to capturedValues by element ID
      markDirty();
      setCapturedValues(prev => ({
        ...prev,
        [voiceTargetId]: prev[voiceTargetId]
          ? prev[voiceTargetId] + ' ' + cleaned
          : cleaned,
      }));
      setVoiceTargetId(null);
    } else if (recTarget?.type === "notepad") {
      // Notepad voice path — unchanged from existing implementation
      insertToQuill(cleaned);
    }
    // If neither branch matches (recTarget is template type from old path), do nothing —
    // template voice is now handled exclusively through voiceTargetId.
  };

  useEffect(
    () => () => {
      if (vr.listening) vr.stop();
    },
    [vr.listening],
  );

  useEffect(() => {
    annotatorRefs.current = annotatorRefs.current.slice(0, notes.length);
  }, [notes.length]);

  const commitAllImages = async () => {
    const copy = [...notes];
    let changed = false;
    for (let idx = 0; idx < copy.length; idx++) {
      const note = copy[idx];
      if (note?.sessionsFieldType !== "image") continue;
      const ref = annotatorRefs.current[idx];
      if (!ref || typeof ref.mergeImage !== "function") continue;
      try {
        const hasStrokes = typeof ref.hasStrokes === "function" && await ref.hasStrokes();
        if (!hasStrokes) continue;
        const merged = await ref.mergeImage();
        if (merged && typeof merged === "string") {
          copy[idx] = { ...note, sessionsImageURL: merged };
          changed = true;
        }
      } catch (e) {
        console.warn("mergeImage failed for index", idx, e);
      }
    }
    if (changed) setNotes(copy);
    return copy;
  };

  // (template section is always mounted so no need to commit on section change)

  // Templates fetch
  useEffect(() => {
    const initializeData = async () => {
      const token = sessionStorage.getItem("user");
      if (!token) return;
      try {
        const decoded = jwtDecode(token);
        const userPractices = decoded.practice;
        if (!userPractices || userPractices.length === 0) return;

        const templatesData = await fetchPracticeTemplates(decoded._id);
        const filteredTemplates = templatesData.filter((template) =>
          template.templatePracticeId.some((p) => userPractices.includes(p)),
        );

        // Dropdown source: hide inactive templates and sort alphabetically.
        // `filteredTemplates` is kept intact so already-selected (possibly inactive)
        // templates still resolve on session reopen.
        const selectableTemplates = filteredTemplates
          .filter((t) => t.templateStatus !== "Inactive")
          .sort((a, b) =>
            (a.templateTitle || "").localeCompare(b.templateTitle || "", undefined, { sensitivity: "base" })
          );

        const initialTemplates = selectableTemplates.filter(
          (t) => t.templateInitial === "initial",
        );
        const followUpTemplates = selectableTemplates.filter(
          (t) => t.templateInitial === "followup",
        );

        // If the session is already attached to an inactive template, surface it
        // in the dropdown as a disabled option so the dropdown shows the
        // template name (instead of falling back to an unrelated option) while
        // still preventing reselection.
        const currentTplForDropdown = templateId
          ? filteredTemplates.find((t) => t._id === templateId)
          : null;
        if (
          currentTplForDropdown &&
          currentTplForDropdown.templateStatus === "Inactive"
        ) {
          const tagged = { ...currentTplForDropdown, _inactiveSelected: true };
          if (currentTplForDropdown.templateInitial === "followup") {
            followUpTemplates.unshift(tagged);
          } else {
            initialTemplates.unshift(tagged);
          }
        }

        setDropdownOptions([
          { label: "Initial Assessment", options: initialTemplates },
          { label: "Follow Up", options: followUpTemplates },
        ]);
        setTemplates(filteredTemplates);

        // If a template is already selected (session reopen), load its pages.
        // Prefer the version the session was filled against, falling back to
        // current pages with a banner if the version snapshot is unavailable.
        const currentTemplateId = templateId || "";
        if (currentTemplateId) {
          const sel = filteredTemplates.find((t) => t._id === currentTemplateId);
          if (sel) {
            await applyTemplatePagesForSession(sel, sessionsTemplateVersionR);
          }
        }
      } catch (error) {
        console.error("Error fetching templates:", error);
      }
    };
    initializeData();
  }, []);

  // ✅ Rehydrate all session state when sessionId changes.
  // Props (sections, templateId, etc.) are intentionally read from closure — they reflect
  // the current render's values and must NOT be in the dep array to avoid re-render loops.
  useEffect(() => {
    const normalized = normalizeNotes(sections);
    setNotes(normalized);
    setSelectedTemplateId(templateId || "");
    initialTemplateIdRef.current = templateId || "";
    dirtyRef.current = false;
    annotatorRefs.current = Array(normalized.length).fill(null);
    setCapturedValues(capturedValuesR || {});
    setServerTimestamp(updatedAtR || null);

    // Reset templatePages for this session's template, honouring the version
    // it was filled against. templates[] is already populated on navigation
    // (fetched on initial mount). On first mount templates is empty — the []
    // fetch effect handles that path.
    if (templates.length > 0) {
      if (templateId) {
        const sel = templates.find((t) => t._id === templateId);
        applyTemplatePagesForSession(sel, sessionsTemplateVersionR);
      } else {
        setTemplatePages([]);
        setTemplateVersion(0);
        setVersionWarning(null);
      }
    }

    // Reset to the tab where this session's content actually lives
    setLocalSection(resolveDefaultSection(templateId, handwritingImageR, notepadContentR, capturedValuesR, sections, mediaFilesR, activeSection));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Backfill capturedValues from sections[] for old sessions (pre-v16) or when stored element IDs
  // no longer match the current template pages (e.g. IDs changed after template edit).
  // Runs whenever templatePages or sessionId changes. Guards ensure it only runs once per session
  // and only when capturedValuesR doesn't already have matching keys.
  useEffect(() => {
    if (!templatePages.length) return;
    const sectionsArr = Array.isArray(sections) ? sections : [];
    if (!sectionsArr.length) return;
    if (lastBackfilledSessionId.current === sessionId) return; // already backfilled this session

    // Collect all page element IDs to check if capturedValuesR keys match
    const pageIds = new Set();
    const collectIds = (items) => {
      for (const item of (items || [])) {
        if (item.kind === 'group') collectIds(item.children);
        else pageIds.add(item.id);
      }
    };
    templatePages.forEach(p => collectIds(p.items));

    const capturedMatchesTemplate = capturedValuesR &&
      Object.keys(capturedValuesR).length > 0 &&
      Object.keys(capturedValuesR).some(k => pageIds.has(k));

    if (capturedMatchesTemplate) return; // capturedValuesR is good — already loaded

    const derived = buildCapturedValuesFromSections(sectionsArr, templatePages);
    if (Object.keys(derived).length > 0) {
      lastBackfilledSessionId.current = sessionId;
      setCapturedValues(derived);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templatePages, sessionId]);

  // Notepad content: once per sessionId
  useEffect(() => {
    loadedNotepadRef.current = false;
  }, [sessionId]);

  useEffect(() => {
    if (loadedNotepadRef.current) return;
    const content =
      safeHtml(notepadContentR) ??
      safeHtml(sections?.notepadContent) ??
      safeHtml(sections?.sessionsNotepadContent) ??
      "";
    setNotepadContent(content);
    loadedNotepadRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Handwriting image: sync with prop when sessionId or prop changes
  useEffect(() => {
    setHandwritingImage(handwritingImageR || null);
  }, [sessionId, handwritingImageR]);

  // Detect mobile device for handwriting button visibility
  useEffect(() => {
    const checkMobile = () => {
      const mobile =
        window.innerWidth <= 1024 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        );
      setIsMobile(mobile);
      setIsNarrow(window.innerWidth <= 1024);
      setIsLandscape(window.matchMedia("(orientation: landscape)").matches);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Template select change
  const [activeNotePage, setActiveNotePage] = useState(0);

  const handleTemplateChange = async (newId) => {
    markDirty();
    // UpdateSession is always editing a saved session — changing the template
    // will overwrite it. Gate the change on an explicit confirm.
    if (newId !== selectedTemplateId) {
      const ok = await confirm({
        title: "Overwrite saved session?",
        message:
          "Changing the template will overwrite this saved session.\n\n" +
          "If you want to record a new session, cancel and use \"Add Note\" instead.",
        confirmLabel: "Overwrite",
        cancelLabel: "Cancel",
        confirmVariant: "danger",
      });
      if (!ok) return;
    }
    commitAllImages();
    setSelectedTemplateId(newId);
    setActiveNotePage(0);
    const sel = templates.find((t) => t._id === newId);
    const pages = sel
      ? (Array.isArray(sel.pages) && sel.pages.length > 0 ? sel.pages : notesToPages(sel.notes))
      : [];
    setTemplatePages(pages);
    setTemplateVersion(sel?.currentVersion || 0);
    setVersionWarning(null);
    // Build initial values from defaultValue on each element
    const init = {};
    for (const page of pages) {
      for (const item of (page.items || [])) {
        if (item.kind === 'group') {
          for (const child of (item.children || [])) {
            if (child.defaultValue !== undefined) init[child.id] = child.defaultValue;
          }
        } else if (item.defaultValue !== undefined) {
          init[item.id] = item.defaultValue;
        }
      }
    }
    setCapturedValues(init);
    setNotes([]); // clear legacy notes state to avoid stale data
    annotatorRefs.current = [];
  };

  // Helpers used in render
  const getFieldValue = (note) => {
    if (!note?.sessionsSectionNotes) return "";
    if (typeof note.sessionsSectionNotes === "object") return "";
    return note.sessionsSectionNotes;
  };
  const handleTemplateFieldChange = (index, value) => {
    setNotes((prev) => {
      const updated = [...prev];
      updated[index].sessionsSectionNotes = value;
      return updated;
    });
  };
  const handleMultiselectChange = (index, option) => {
    setNotes((prev) =>
      prev.map((note, i) => {
        if (i !== index) return note;
        const current = note.sessionsSectionNotes?.selectedOptions || [];
        const selected = current.includes(option)
          ? current.filter((x) => x !== option)
          : [...current, option];
        return {
          ...note,
          sessionsSectionNotes: {
            ...note.sessionsSectionNotes,
            selectedOptions: selected,
          },
        };
      }),
    );
  };

  // Core save logic — returns updated processedNotes; callers handle toast/navigation.
  // A save already running is awaited rather than started again. Submit,
  // Email and Download all call this, and the Submit button used to hand
  // itself back before its own navigation had happened — two saves could be
  // in flight against the same session at once. Callers await the same
  // promise, so a second caller still sees the save complete before it
  // continues (returning early would have opened the email modal against
  // content that had not been written yet).
  const inFlightSaveRef = useRef(null);

  const performSave = (...args) => {
    if (inFlightSaveRef.current) return inFlightSaveRef.current;
    const run = performSaveInner(...args);
    inFlightSaveRef.current = run;
    return run.finally(() => {
      inFlightSaveRef.current = null;
    });
  };

  const performSaveInner = async () => {
    let currentHandwriting = handwritingImage;
    // Only meaningful while the canvas is on screen — exportImage fails on
    // display:none. When it is hidden, `handwritingImage` is already current:
    // onStroke commits every stroke as it is drawn, which is what stopped
    // handwriting going missing when the section changed without passing
    // through handleSectionChange.
    if (localSection === "write" && inlineWriteRef.current) {
      const captured = await inlineWriteRef.current.flush();
      if (captured) { currentHandwriting = captured; setHandwritingImage(captured); }
    }

    const mergedNotes = await commitAllImages();

    const processedNotes = [];
    for (let i = 0; i < (mergedNotes || []).length; i++) {
      const n = mergedNotes[i];
      if (n.sessionsFieldType !== "image") {
        processedNotes.push(n);
        continue;
      }
      const url = typeof n.sessionsImageURL === "string" ? n.sessionsImageURL : "";
      if (url && url.startsWith("data:")) {
        const blob = await fetch(url).then((r) => r.blob());
        const fd = new FormData();
        fd.append("image", blob);
        fd.append("x-image-caption", "Session Image Upload");
        fd.append("patientId", patientId);
        fd.append("appointmentId", appointmentId);
        fd.append("type", "session");
        const up = await uploadImage(fd);
        processedNotes.push({ ...n, sessionsImageURL: up.imageUrl });
      } else {
        processedNotes.push(n);
      }
    }

    const finalMediaFiles = [];
    for (let idx = 0; idx < (mediaImages || []).length; idx++) {
      const m = mediaImages[idx];
      if (typeof m !== "string") continue;
      if (m.startsWith("data:")) {
        const blob = await fetch(m).then((r) => r.blob());
        const fd = new FormData();
        fd.append("image", blob);
        fd.append("x-image-caption", `Media Photo ${idx + 1}`);
        fd.append("patientId", patientId);
        fd.append("appointmentId", appointmentId);
        fd.append("type", "session");
        const up = await uploadImage(fd);
        finalMediaFiles.push({
          fileName: `media-photo-${idx + 1}`,
          fileUrl: up.imageUrl,
          fileType: "image",
          uploadedAt: new Date().toISOString(),
          description: `Captured photo ${idx + 1}`,
        });
      } else if (/^https?:\/\//.test(m)) {
        finalMediaFiles.push({
          fileName: `media-photo-${idx + 1}`,
          fileUrl: m,
          fileType: "image",
          uploadedAt: new Date().toISOString(),
          description: `Captured photo ${idx + 1}`,
        });
      }
    }
    const seen = new Set();
    const mediaFiles = finalMediaFiles.filter((f) => {
      const u = f.fileUrl;
      if (!u || seen.has(u)) return false;
      seen.add(u);
      return true;
    });

    const includeTemplateChange =
      selectedTemplateId && selectedTemplateId !== initialTemplateIdRef.current;

    const payload = {
      sessionsPatientId: patientId,
      sessionsPracticeId: practiceId,
      sessionsAppointmentId: appointmentId,
      sessionsServiceName: serviceName,
      sessionsResourceId: resourceId,
      sessionsAppointmentStart: appointmentStart,
      ...(sessionType ? { sessionType } : {}),
      ...(includeTemplateChange ? { sessionsTemplateId: selectedTemplateId } : {}),
      sections: processedNotes,
      ...(notepadContent?.trim() ? { notepadContent, notepadContentType: "html" } : {}),
      ...(currentHandwriting ? { handwritingImage: currentHandwriting } : {}),
      ...(mediaFiles.length ? { mediaFiles } : {}),
      capturedValues,
      sessionsTemplateVersion: templateVersion,
      clientTimestamp: serverTimestamp,
      templatePages,
    };

    let resp;
    try {
      resp = await updateSessionById(sessionId, payload);
    } catch (err) {
      // 409 = stale write: DB was updated after our last known timestamp.
      // Retry once with the server's current timestamp so we don't block the user.
      const serverTs = err?.response?.data?.serverUpdatedAt;
      if (err?.response?.status === 409 && serverTs) {
        payload.clientTimestamp = serverTs;
        resp = await updateSessionById(sessionId, payload);
      } else {
        throw err;
      }
    }
    setServerTimestamp(resp?.updatedAt || null);
    initialTemplateIdRef.current = selectedTemplateId;
    setNotes(processedNotes);
    return processedNotes;
  };

  useImperativeHandle(ref, () => ({
    /**
     * Persist without navigating — used when the user starts a SECOND note
     * (Add) or a SyncTo recording while this one has unsaved edits. The note
     * already exists here, so this is always a plain update.
     */
    async saveNow() {
      // Commit an in-progress drawing first — the strokes aren't in state yet,
      // so dirtyRef alone would let a half-finished sketch be thrown away.
      if (localSection === "write" && inlineWriteRef.current) {
        if (await inlineWriteRef.current.hasStrokes()) {
          const captured = await inlineWriteRef.current.flush();
          if (captured) {
            setHandwritingImage(captured);
            dirtyRef.current = true;
          }
        }
      }
      // null means "nothing was written" — callers use it to skip refreshing
      // lists that can't have changed.
      if (!dirtyRef.current) return null;
      try {
        await performSave();
        dirtyRef.current = false;
        return sessionId;
      } catch (err) {
        console.error("UpdateSession saveNow failed", err);
        return null;
      }
    },
    hasUnsavedContent: () => dirtyRef.current,
  }));

  // The finishing save queues its navigation on a timer, so the timer has to
  // be cancellable: without this the editor could unmount (first navigation)
  // and a second queued navigation would still fire from the unmounted tree,
  // moving the user off whatever screen they had just landed on.
  const navTimerRef = useRef(null);
  useEffect(() => () => {
    if (navTimerRef.current) {
      clearTimeout(navTimerRef.current);
      // Nulled as well as cleared: handleSubmit refuses to run while this is
      // set, so a stale id left behind is a second way to wedge the button.
      navTimerRef.current = null;
    }
  }, []);

  /**
   * The explicit save.
   *
   * `mode` is "save" (stay here, refresh the list) or "invoice" (save, then
   * hand off into the invoice for this appointment). Saving used to always
   * navigate away, to the invoice or the calendar, which turned "save my
   * note" into "leave the patient". The hand-off is now its own button, and
   * plain Save keeps the user exactly where they are.
   *
   * The implicit saves (Add, switching note, starting a recording) go through
   * saveNow() instead and never reach this.
   */
  const handleSubmit = async (mode = "save") => {
    // Belt and braces alongside `disabled={isSaving}`: the button is also
    // reachable from a keyboard repeat and from the imperative handle.
    if (isSaving || navTimerRef.current) return;
    setIsSaving(true);
    try {
      await performSave();
      // Opt-in appointment completion. Only when ticked, this is linked to
      // an appointment, and that appointment isn't already Completed.
      // "New Patient" to "Completed" is blocked server-side; skip the call so
      // we don't surface an avoidable 409 to the user.
      if (
        completeAppointment &&
        appointmentId &&
        !!appointmentStatus &&
        appointmentStatus !== "Completed" &&
        appointmentStatus !== "New Patient"
      ) {
        try {
          await updateAppointmentStatusByType(appointmentId, "Completed");
          onAppointmentCompleted?.();
        } catch (statusErr) {
          console.error("Failed to mark appointment complete:", statusErr);
        }
      }
      stopStream();
      dirtyRef.current = false;
      showToast("success", "Note saved.");

      if (mode !== "invoice" || !canInvoice) {
        onSaved?.(sessionId, { mode: "save" });
        setIsSaving(false);
        return;
      }

      navTimerRef.current = setTimeout(async () => {
        navTimerRef.current = null;
        /*
         * Whatever happens below, hand the button back. isSaving also
         * disables Download and Email, so leaving it set after a save that
         * succeeded would wedge the whole editor until the user navigated
         * away and back. Clearing it on an unmounted component is a no-op in
         * React 18, so the branch that really does unmount loses nothing.
         */
        try {
          // The account isn't in scope here (unlike the create editor), so
          // resolve it only on the finishing save rather than on every mount.
          let account = null;
          try {
            account = await getAccountByPatientId(patientId);
          } catch (_err) {
            account = null;
          }
          if (appointmentId && !alreadyInvoiced && account?._id) {
            Navigate(`/invoices/1/${account._id}`, {
              state: {
                servicesId: serviceId || undefined,
                appointmentId,
                appointmentStart: appointmentStart || null,
                patientId,
                practiceId,
                practitionerId: resourceId || null,
              },
            });
            return;
          }
          Navigate(getBookingsReturnPath());
        } catch (err) {
          // Saving already succeeded; only the hand-off failed. Say so rather
          // than leaving the user to guess whether the note survived.
          console.error("Post-save navigation failed", err);
          showToast("error", "The note was saved, but the invoice could not be opened.");
        } finally {
          setIsSaving(false);
        }
      }, 800);
    } catch (e) {
      console.error("UpdateSession save failed", e);
      showToast("error", "The note could not be saved. Please try again.");
      setIsSaving(false);
    }
  };

  // Download — open the Download Options modal; actual download happens after confirm
  const handleSaveAndDownload = () => {
    if (!sessionId || !selectedTemplateId || !templatePages.length) return;
    setShowDownloadOptions(true);
  };

  // Confirm Download — save first, then download PDF with chosen options.
  // Save first so the PDF reflects what is on screen, then download. The
  // user stays on the note.
  const handleConfirmDownload = async () => {
    if (!sessionId || !selectedTemplateId || !templatePages.length) return;
    setShowDownloadOptions(false);
    setPdfExporting(true);
    try {
      await performSave();
      await exportSessionTemplatePdf(sessionId, { ...pdfOptions, templateTitle: pdfTemplateTitle });
      dirtyRef.current = false;
      onSaved?.(sessionId, { mode: "save" });
    } catch (err) {
      console.error("Save and download failed:", err);
      showToast("error", "Failed to save or download");
    } finally {
      setPdfExporting(false);
    }
  };

  // Email — save first, then show email form (with report template prefilled)
  const handleEmailClick = async () => {
    if (!sessionId || !selectedTemplateId || !templatePages.length) return;
    setIsSaving(true);
    try {
      await performSave();
      const { subject, message, ccSender, ccAddress } = await buildReportEmailPrefill();
      setEmailSubject(subject || "Session Notes");
      setEmailMessage(message || "");
      setEmailTo("");
      // Combine the sender CC (if enabled) with any configured CC addresses
      // from the report template. The user can still edit the field below.
      const ccParts = [];
      if (ccSender) {
        try {
          const token = sessionStorage.getItem("user");
          const senderEmail = token ? (jwtDecode(token)?.email || "") : "";
          if (senderEmail) ccParts.push(senderEmail);
        } catch (e) { /* ignore decode errors */ }
      }
      if (ccAddress) {
        ccAddress
          .split(/[,;]/)
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((addr) => {
            if (!ccParts.some((p) => p.toLowerCase() === addr.toLowerCase())) {
              ccParts.push(addr);
            }
          });
      }
      setEmailCc(ccParts.join("; "));
      setToSuggestOpen(false);
      // Load people for the "To" typeahead — scoped server-side to the
      // logged-in user's practice(s). getPeopleForUser throws on 404 (none).
      try {
        const token = sessionStorage.getItem("user");
        const list = token ? await getPeopleForUser(token) : [];
        setPeople(Array.isArray(list) ? list : []);
      } catch (e) {
        setPeople([]);
      }
      setShowEmailForm(true);
    } catch (err) {
      console.error("Save before email failed:", err);
      showToast("error", "Failed to save session");
    } finally {
      setIsSaving(false);
    }
  };

  // Resolve report email template variables for this session.
  // Returns { subject, message } as plain text (HTML stripped) or empty strings if no template.
  const buildReportEmailPrefill = async () => {
    try {
      const [finCommsList, patient, practices] = await Promise.all([
        practiceId ? getFinComms(practiceId) : Promise.resolve([]),
        patientId ? getPatientById(patientId) : Promise.resolve(null),
        practiceId ? getPracticesByNumbers([practiceId]) : Promise.resolve([]),
      ]);

      const fin = Array.isArray(finCommsList) ? finCommsList[0] : null;
      const subjectTpl = fin?.report?.subject ?? fin?.reportSubject ?? "";
      const bodyTplHtml = fin?.report?.text ?? fin?.reportText ?? "";
      const ccSender = !!(fin?.report?.ccSender ?? fin?.reportCcSender);
      const ccAddress = String(fin?.report?.ccAddress ?? fin?.reportCcAddress ?? "").trim();
      if (!subjectTpl && !bodyTplHtml) return { subject: "", message: "", ccSender, ccAddress };

      const practice = Array.isArray(practices) ? practices[0] : practices;
      const tpl = templates.find((t) => t._id === selectedTemplateId);
      const sessionDateStr = (appointmentStart || "").split("T")[0] || "";
      const patientFull = [patient?.patientFirstName, patient?.patientLastName]
        .filter(Boolean).join(" ").trim();

      const ctx = {
        patientName: patientFull,
        sessionDate: sessionDateStr,
        templateName: tpl?.templateTitle || "",
        practiceName: practice?.practiceName || "",
        practicePhone: practice?.practicePhoneNumber || "",
        practiceEmail: practice?.practiceEmail || "",
      };

      const fill = (tplStr) =>
        String(tplStr || "").replace(/\$\{\s*([^}]+?)\s*\}/g, (_, key) => {
          const v = ctx[key.trim()];
          return v === undefined || v === null ? "" : String(v);
        });

      const htmlToText = (html) =>
        String(html || "")
          .replace(/<\s*br\s*\/?>/gi, "\n")
          .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
          .replace(/<li[^>]*>/gi, "• ")
          .replace(/<[^>]+>/g, "")
          .replace(/&nbsp;/g, " ")
          .replace(/&amp;/g, "&")
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/\n{3,}/g, "\n\n")
          .trim();

      return {
        subject: fill(subjectTpl),
        message: htmlToText(fill(bodyTplHtml)),
        ccSender,
        ccAddress,
      };
    } catch (err) {
      console.error("Failed to build report email prefill:", err);
      return { subject: "", message: "", ccSender: false, ccAddress: "" };
    }
  };

  // --- "To" field typeahead -------------------------------------------------
  // The active query is the address segment currently being typed: everything
  // after the last ";" so earlier committed recipients are left untouched.
  const toQuery = (() => {
    const idx = emailTo.lastIndexOf(";");
    return (idx >= 0 ? emailTo.slice(idx + 1) : emailTo).trim().toLowerCase();
  })();

  const toSuggestions =
    toSuggestOpen && toQuery.length >= 1
      ? people
          .filter((p) => {
            const emails = personEmails(p);
            if (!emails.length) return false; // nothing to fill — skip
            const first = (p.firstName || "").toLowerCase();
            const last = (p.lastName || "").toLowerCase();
            const primary = emails[0].toLowerCase();
            return (
              first.includes(toQuery) ||
              last.includes(toQuery) ||
              primary.includes(toQuery)
            );
          })
          .slice(0, 8)
      : [];

  // Pick a person: their primary email goes to "To", any extra emails go to CC.
  const handlePickPerson = (p) => {
    const emails = personEmails(p);
    if (!emails.length) return;
    const [primary, ...extras] = emails;

    // Replace the partial segment being typed with the chosen primary email,
    // preserving any recipients already committed before the last ";".
    const idx = emailTo.lastIndexOf(";");
    const head = idx >= 0 ? emailTo.slice(0, idx) : "";
    const toAddrs = head.split(";").map((s) => s.trim()).filter(Boolean);
    if (!toAddrs.includes(primary)) toAddrs.push(primary);
    setEmailTo(toAddrs.join("; "));

    // Append extra emails to CC (deduped, and not already a "To" recipient).
    if (extras.length) {
      const ccAddrs = emailCc.split(";").map((s) => s.trim()).filter(Boolean);
      extras.forEach((e) => {
        if (!ccAddrs.includes(e) && !toAddrs.includes(e)) ccAddrs.push(e);
      });
      setEmailCc(ccAddrs.join("; "));
    }

    setToSuggestOpen(false);
  };

  // Send email — close modal immediately, send in background, show toast + nav notification on result
  const handleSendEmail = () => {
    if (!emailTo.trim()) {
      showToast("error", "Please enter a recipient email address");
      return;
    }
    const to = emailTo.trim();
    const payload = {
      to,
      cc: emailCc.trim(),
      subject: emailSubject.trim() || "Session Notes",
      message: emailMessage.trim(),
      options: { ...pdfOptions, templateTitle: pdfTemplateTitle },
    };
    setShowEmailForm(false);
    emailSessionTemplatePdf(sessionId, payload)
      .then(() => {
        showToast("success", "Email sent successfully!");
        if (typeof addNotification === "function") {
          addNotification({
            message: `Session notes PDF emailed to ${to}`,
            timestamp: Date.now(),
            type: "email",
          });
        }
      })
      .catch((err) => {
        console.error("Email send failed:", err);
        showToast("error", "Failed to send email");
      });
    // The email continues in the background and toasts when done; the user
    // stays on the note.
    onSaved?.(sessionId, { mode: "save" });
  };

  const renderCurrentSection = () => {
    switch (localSection) {
      case "apptNotepad":
        return (
          <div className="mt-1">
            <Row className="align-items-center mb-1">
              <Col md={12}>
                <div className="d-flex align-items-center">
                  <div>
                    <p className="text-muted mb-0">
                      Use this space for quick notes during the session.
                    </p>
                  </div>
                  <div className="d-flex align-items-center ms-auto gap-1">
                    <OverlayTrigger placement="top" overlay={<Tooltip>Speak to add to notepad</Tooltip>}>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="p-1 border-0"
                        aria-label="Speak to add to notepad"
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); openRecorderForNotepad(); }}
                      >
                        <i className="fa-light fa-microphone fs-6" />
                      </Button>
                    </OverlayTrigger>
                    <OverlayTrigger placement="top" overlay={<Tooltip>{isFullScreen ? "Collapse" : "Expand"}</Tooltip>}>
                      <Button variant="link" className="text-primary text-decoration-none p-0 px-2" onClick={handleExpand} aria-label={isFullScreen ? "Collapse" : "Expand"}>
                        <i className={`fa-light ${isFullScreen ? "fa-arrows-minimize" : "fa-arrows-maximize"} fs-5`} />
                      </Button>
                    </OverlayTrigger>
                  </div>
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <div className="bg-white rounded shadow-sm notepad-container">
                  <ReactQuill
                    key={`quill-${sessionId}`}
                    ref={quillRef}
                    theme="snow"
                    readOnly={false}
                    value={notepadContent}
                    onChange={(html) => { markDirty(); setNotepadContent(html); }}
                    modules={{
                      toolbar: [
                        ["bold", "italic", "underline"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        ["link"],
                      ],
                    }}
                    formats={["bold", "italic", "underline", "list", "bullet", "link"]}
                    style={{ minHeight: "60vh", height: "60vh" }}
                    placeholder="Start typing your notes here..."
                  />
                </div>
              </Col>
            </Row>
          </div>
        );

      case "apptMedia":
        return (
          <div className="mt-1">
            <Row className="align-items-center mb-3">
              <Col>
              </Col>
              <Col xs="auto" className="d-flex align-items-center gap-1">
                <OverlayTrigger placement="top" overlay={<Tooltip>{isFullScreen ? "Collapse" : "Expand"}</Tooltip>}>
                  <Button variant="link" className="text-primary text-decoration-none p-0 px-2" onClick={handleExpand} aria-label={isFullScreen ? "Collapse" : "Expand"}>
                    <i className={`fa-light ${isFullScreen ? "fa-arrows-minimize" : "fa-arrows-maximize"} fs-5`} />
                  </Button>
                </OverlayTrigger>
              </Col>
            </Row>

            {camError ? (
              <Alert variant="warning" className="mb-3">
                Camera access failed ({camError}). You can still upload from gallery:
                <Form.Control
                  className="mt-2"
                  type="file"
                  accept="image/*"
                  multiple
                  capture="environment"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    files.forEach((file) => {
                      const reader = new FileReader();
                      reader.onload = () => setMediaImages((prev) => [reader.result, ...prev]);
                      reader.readAsDataURL(file);
                    });
                    e.target.value = "";
                  }}
                />
              </Alert>
            ) : (
              <div className="rounded right-scroll shadow-sm mb-3" style={{ position: "relative" }}>
                <video ref={videoRef} playsInline muted style={{ width: "100%", height: "auto", background: "#000" }} />
                <div className="position-absolute bottom-0 start-0 end-0 d-flex justify-content-center gap-3 p-3">
                  <Button variant="light" className="rounded-circle" onClick={capturePhoto} aria-label="Capture">
                    <i className="fa-solid fa-camera" />
                  </Button>
                  <Button variant="light" className="rounded-circle" onClick={flipCamera} aria-label="Flip">
                    <i className="fa-solid fa-arrows-rotate" />
                  </Button>
                </div>
              </div>
            )}

            {mediaImages.length > 0 && (
              <>
                <h6 className="text-primary">Photos ({mediaImages.length})</h6>
                <Row>
                  {mediaImages.map((url, idx) => (
                    <Col xs={4} sm={3} md={2} key={idx} className="mb-3">
                      <div className="position-relative border rounded right-scroll">
                        <img
                          src={url}
                          alt={`photo ${idx + 1}`}
                          style={{ width: "100%", height: 100, objectFit: "cover", cursor: "pointer" }}
                          onClick={() => setLightboxUrl(url)}
                          title="Click to enlarge"
                        />
                        <Button variant="danger" size="sm" className="position-absolute top-0 end-0 m-1 p-1" onClick={() => removePhoto(idx)} aria-label="Remove" title="Remove">
                          ×
                        </Button>
                      </div>
                    </Col>
                  ))}
                </Row>
              </>
            )}

            <Modal show={!!lightboxUrl} onHide={() => setLightboxUrl(null)} centered size="lg">
              <Modal.Header closeButton />
              <Modal.Body className="p-0 text-center bg-dark">
                {lightboxUrl && (
                  <img
                    src={lightboxUrl}
                    alt="Full size"
                    style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }}
                  />
                )}
              </Modal.Body>
            </Modal>
          </div>
        );

      default:
        return null; // apptTemplate rendered separately (always mounted)
    }
  };

  const outerStyle = isFullScreen
    ? { position: "fixed", inset: 0, zIndex: 1049, background: "white", overflowY: "auto", display: "flex", flexDirection: "column" }
    : { marginTop: "4px" };

  // A legacy bundle holds several kinds in one document, so it keeps a row of
  // tabs to move between them. A single-kind note (every note written since
  // the split) has nothing to switch and shows no tabs at all.
  const kindTabs = legacyBundle ? (
    <div className="d-flex flex-wrap gap-1 px-2 pt-2">
      {navItems.map(({ key, label, icon }) => (
        <Button
          key={key}
          size="sm"
          variant={localSection === key ? "primary" : "outline-primary"}
          className="d-flex align-items-center"
          style={{ minWidth: 120 }}
          onClick={() => handleSectionChange(key)}
        >
          <i className={`${icon} me-2`} />
          {label}
        </Button>
      ))}
    </div>
  ) : null;

  const sessionContent = (
    <div style={outerStyle}>
      <AppToast show={toast.show} onClose={hideToast} variant={toast.variant} message={toast.message} />
      <ConfirmModal {...confirmProps} />

      <div className="d-flex flex-column flex-grow-1">
        {kindTabs}

        {/* Section content */}
        <div className="p-2 flex-grow-1">
          {/* Write section always mounted so canvas state persists across section switches */}
          <div style={{ display: localSection === "write" ? "block" : "none" }}>
            <InlineWriteSection
              ref={inlineWriteRef}
              onSave={(img) => { markDirty(); setHandwritingImage(img); }}
              existingImage={handwritingImage}
              onExpand={handleExpand}
              isFullScreen={isFullScreen}
            />
          </div>
          {/* Template section always mounted so ImageAnnotator canvas state persists */}
          <div style={{ display: localSection === "apptTemplate" ? "block" : "none" }}>
            <Row className="mb-3 mt-1 align-items-end g-2">
              <Col xs={12} md style={{ minWidth: 0 }}>
                {/* Label and select on one line. The label matches the
                    sitting date on the notes list (.acc-notes-sitting-date:
                    semibold, 0.85rem) so the editor and the list read as one. */}
                <Form.Group controlId="formGridSessionTemplate" className="d-flex align-items-center gap-2">
                  <Form.Label className="mb-0 text-primary fw-semibold text-nowrap" style={{ fontSize: "0.85rem" }}>
                    Template
                  </Form.Label>
                  <Form.Select
                    value={selectedTemplateId}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                  >
                    <option value="" disabled>Choose a template...</option>
                    {dropdownOptions.map((category, idx) => (
                      <React.Fragment key={idx}>
                        <option disabled className="text-primary fw-bold">{category.label}</option>
                        {category.options.map((template) => (
                          <option
                            key={template._id}
                            value={template._id}
                            disabled={!!template._inactiveSelected}
                          >
                            {template.templateTitle}
                            {template._inactiveSelected ? " (Inactive)" : ""}
                          </option>
                        ))}
                      </React.Fragment>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              {/* Icon-only actions with tooltips (CLAUDE.md 1.4). Download and
                  Email save first, then act, and exist only for a saved
                  template note, which is the one kind the PDF renderer knows. */}
              <Col xs="auto" className="d-flex align-items-center gap-2 pb-1">
                {sessionId && selectedTemplateId && templatePages.length > 0 && (
                  <>
                    <OverlayTrigger placement="top" overlay={<Tooltip>Save and download as PDF</Tooltip>}>
                      <Button
                        variant="link"
                        className="text-primary text-decoration-none p-0 acc-notes-row-icon"
                        aria-label="Save and download as PDF"
                        disabled={pdfExporting || isSaving}
                        onClick={handleSaveAndDownload}
                      >
                        {pdfExporting
                          ? <Spinner size="sm" animation="border" />
                          : <i className="fa-solid fa-download" />}
                      </Button>
                    </OverlayTrigger>
                    <OverlayTrigger placement="top" overlay={<Tooltip>Save and email as PDF</Tooltip>}>
                      <Button
                        variant="link"
                        className="text-primary text-decoration-none p-0 acc-notes-row-icon"
                        aria-label="Save and email as PDF"
                        disabled={isSaving}
                        onClick={handleEmailClick}
                      >
                        <i className="fa-solid fa-envelope" />
                      </Button>
                    </OverlayTrigger>
                  </>
                )}
                <OverlayTrigger placement="top" overlay={<Tooltip>{isFullScreen ? "Collapse" : "Expand"}</Tooltip>}>
                  <Button
                    variant="link"
                    className="text-primary text-decoration-none p-0 acc-notes-row-icon"
                    aria-label={isFullScreen ? "Collapse" : "Expand"}
                    onClick={handleExpand}
                  >
                    <i className={`fa-light ${isFullScreen ? "fa-arrows-minimize" : "fa-arrows-maximize"}`} />
                  </Button>
                </OverlayTrigger>
              </Col>
            </Row>

            {selectedTemplateId && templatePages.length > 0 ? (
              <>
                {versionWarning && (
                  <Alert variant="warning" className="py-2 small mb-2">
                    This session was filled using template version{" "}
                    <strong>{versionWarning.storedVersion}</strong>, but that
                    version snapshot is no longer available. Showing the current
                    template instead, so some fields may not match the original.
                  </Alert>
                )}
                <NoteRenderer
                  mode="fill"
                  pages={templatePages}
                  values={capturedValues}
                  onChange={(elementId, value) => {
                    markDirty();
                    setCapturedValues(prev => ({ ...prev, [elementId]: value }));
                  }}
                  onVoiceRequest={(elementId) => {
                    setVoiceTargetId(elementId);
                    setRecOpen(true);
                  }}
                />
              </>
            ) : selectedTemplateId ? (
              <p className="text-muted small">This template has no pages defined.</p>
            ) : null}
          </div>
          {localSection !== "write" && localSection !== "apptTemplate" && renderCurrentSection()}
        </div>

        {/* Footer: the explicit saves. Save keeps the user here; Save and
            invoice is the hand-off into billing, offered only when the Notes
            section says this appointment can still be invoiced. Both share
            one width so they read as a matched pair. */}
        <div className="acc-notes-editor-footer d-flex align-items-center flex-wrap gap-2 p-2">
          {/* Only when the appointment's status is KNOWN and not final. A
              previous appointment's status arrives after a fetch; until then
              nothing is offered rather than a checkbox that may be wrong. */}
          {appointmentId &&
            !!appointmentStatus &&
            appointmentStatus !== "Completed" &&
            appointmentStatus !== "New Patient" && (
              <Form.Check
                type="checkbox"
                id="editsession-complete-appointment"
                className="mb-0"
                label="Complete appointment"
                checked={completeAppointment}
                onChange={(e) => setCompleteAppointment(e.target.checked)}
              />
            )}
          <div className="d-flex align-items-center flex-wrap gap-2 ms-auto acc-notes-editor-actions">
            <Button
              variant="primary"
              style={{ width: NOTE_ACTION_WIDTH }}
              onClick={() => handleSubmit("save")}
              disabled={isSaving}
            >
              {isSaving ? (
                <Spinner size="sm" animation="border" />
              ) : (
                <>
                  <i className="fa-regular fa-floppy-disk me-2" />
                  Save
                </>
              )}
            </Button>
            {canInvoice && (
              <Button
                variant="outline-primary"
                style={{ width: NOTE_ACTION_WIDTH }}
                onClick={() => handleSubmit("invoice")}
                disabled={isSaving}
              >
                <i className="fa-solid fa-file-invoice-dollar me-2" />
                Save and invoice
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Email Session Notes Modal */}
      <Modal show={showEmailForm} onHide={() => setShowEmailForm(false)} size="lg" centered>
        <Modal.Header
          closeButton
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">
            <i className="fa-light fa-envelope me-2" />
            Email Session Notes
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-2 mb-2">
            <Col md={6}>
              <Form.Group className="position-relative">
                <Form.Label>To <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  autoComplete="off"
                  placeholder="Type a name, or enter email(s) separated by ;"
                  value={emailTo}
                  onChange={(e) => {
                    setEmailTo(e.target.value);
                    setToSuggestOpen(true);
                  }}
                  onFocus={() => setToSuggestOpen(true)}
                  onBlur={() => setTimeout(() => setToSuggestOpen(false), 150)}
                />
                {toSuggestions.length > 0 && (
                  <div
                    className="list-group position-absolute w-100 shadow-sm"
                    style={{ zIndex: 1056, maxHeight: "240px", overflowY: "auto" }}
                  >
                    {toSuggestions.map((p) => {
                      const emails = personEmails(p);
                      const extra = emails.length - 1;
                      return (
                        <button
                          key={p._id?.$oid || p._id || `${p.firstName}-${p.lastName}-${emails[0]}`}
                          type="button"
                          className="list-group-item list-group-item-action py-2"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handlePickPerson(p);
                          }}
                        >
                          <div className="fw-semibold text-truncate">
                            {personDisplayName(p) || emails[0]}
                          </div>
                          <div className="small text-muted text-truncate">
                            {emails[0]}
                            {extra > 0 && (
                              <span className="ms-1">
                                (+{extra} to CC)
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>CC</Form.Label>
                <Form.Control
                  type="text"
                  autoComplete="off"
                  placeholder="cc@example.com (separate multiple with ;)"
                  value={emailCc}
                  onChange={(e) => setEmailCc(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-2">
            <Form.Label>Subject</Form.Label>
            <Form.Control
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={12}
              placeholder="The session notes PDF will be attached to this email."
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              style={{ minHeight: '14rem' }}
            />
          </Form.Group>
          <hr className="my-3" />
          <div className="mb-2 fw-bold small text-muted">Include in PDF</div>
          <PdfOptionCheckboxes
            options={pdfOptions}
            setOptions={setPdfOptions}
            templateTitle={pdfTemplateTitle}
            setTemplateTitle={setPdfTemplateTitle}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowEmailForm(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSendEmail} disabled={!emailTo.trim()}>
            <i className="fa-regular fa-envelope me-1" />Send Email
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Download Options Modal */}
      <Modal show={showDownloadOptions} onHide={() => setShowDownloadOptions(false)} centered>
        <Modal.Header
          closeButton
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">Download Options</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2 fw-bold small text-muted">Include in PDF</div>
          <PdfOptionCheckboxes
            options={pdfOptions}
            setOptions={setPdfOptions}
            templateTitle={pdfTemplateTitle}
            setTemplateTitle={setPdfTemplateTitle}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDownloadOptions(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmDownload} disabled={pdfExporting || isSaving}>
            <i className="fa fa-download me-1" />Download
          </Button>
        </Modal.Footer>
      </Modal>

      <RecorderModal
        show={recOpen}
        onClose={closeRecorder}
        onDone={applyTranscriptToTarget}
        vr={vr}
      />
    </div>
  );

  return isFullScreen
    ? createPortal(sessionContent, document.body)
    : sessionContent;
});
